// Client-side download helpers for the player's Download button.
//
// - Direct files (mp4/webm/mov)      -> /api/download?dl=1 (edge sets attachment)
// - HLS (.m3u8, incl. TeraBox tbstream) -> fetch playlist, grab every segment,
//   assemble in-browser, save as .mp4 (fMP4) or .ts (MPEG-TS), with progress.

import type { ResolveResult } from './api'
import { apiUrl } from './apiBase'

export type DownloadProgress = (percent: number, message: string) => void

const noop: DownloadProgress = () => {}

// Same-origin API paths (tbstream) go direct; anything else through the edge
// download proxy so CORS never blocks the segment fetches.
function proxied(u: string): string {
  return u.startsWith('/')
    ? apiUrl(u)
    : apiUrl(`/api/download?u=${encodeURIComponent(u)}`)
}

function absolute(u: string, base: string): string {
  try {
    return new URL(u, new URL(base, location.href)).toString()
  } catch {
    return u
  }
}

export function sanitizeFilename(title: string | undefined, ext: string): string {
  const base = (title || 'video')
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
    .slice(0, 80) || 'video'
  return /\.[a-z0-9]{2,5}$/i.test(base) ? base : `${base}.${ext}`
}

function saveBlob(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objectUrl
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60000)
}

// --- iPhone: Safari's a[download] lands in Files, never in Photos. ----------
// On iOS we hand the finished file to the Web Share sheet instead, so the
// user taps "Save Video" and it goes straight into the Photos app.
let pendingFile: File | null = null

function isIOS(): boolean {
  return /iP(hone|od|ad)/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

function shareFilesSupported(): boolean {
  const nav = navigator as any
  return typeof nav.share === 'function' && typeof nav.canShare === 'function'
}

function makeFile(blob: Blob, filename: string): File {
  try {
    return new File([blob], filename, { type: blob.type || 'video/mp4' })
  } catch {
    const anyBlob = blob as any
    anyBlob.name = filename
    return anyBlob as File
  }
}

// Save via the iOS share sheet (-> Photos) or the plain browser download.
function saveSmart(blob: Blob, filename: string): { ok: true; needsShare?: boolean } {
  if (isIOS() && shareFilesSupported()) {
    const file = makeFile(blob, filename)
    const nav = navigator as any
    try {
      if (nav.canShare({ files: [file] })) {
        pendingFile = file
        return { ok: true, needsShare: true }
      }
    } catch { /* fall through to regular download */ }
  }
  saveBlob(blob, filename)
  return { ok: true }
}

// Second tap ("Save to Photos") opens the native share sheet.
// Must run inside a fresh user gesture - never await before share().
// fromTap=false is the automatic attempt right after a download (may be
// rejected for lacking a fresh gesture); fromTap=true is a real button tap.
export async function sharePending(fromTap = false): Promise<{ ok: boolean; cancelled?: boolean; needsTap?: boolean; message?: string }> {
  const file = pendingFile
  if (!file) return { ok: false, message: 'File ready nei — abar download korun.' }
  const nav = navigator as any
  try {
    await nav.share({ files: [file] })
    pendingFile = null
    return { ok: true }
  } catch (e: any) {
    if (e && e.name === 'AbortError') {
      return { ok: false, cancelled: true } // sheet dismissed - keep file ready
    }
    if (e && (e.name === 'NotAllowedError' || e.name === 'InvalidStateError' || e.name === 'SecurityError')) {
      if (!fromTap) {
        // No fresh user activation yet - keep the file for the tap button.
        return { ok: false, needsTap: true }
      }
      // Fresh tap still rejected -> give up to the browser download (Files).
      pendingFile = null
      saveBlob(file, file.name || 'video.mp4')
      return { ok: true, message: 'Share blocked — Files app-e save hoyeche (Photos-e na).' }
    }
    // Share unsupported -> fall back to browser download (Files app).
    pendingFile = null
    saveBlob(file, file.name || 'video.mp4')
    return { ok: true, message: 'Share supported na — Files app-e save hoyeche.' }
  }
}

async function fetchOk(url: string): Promise<Response> {
  const res = await fetch(proxied(url), { cache: 'no-store' })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res
}

function attrNumber(line: string, name: string): number {
  const m = line.match(new RegExp(name + '=(-?\\d+)'))
  return m ? Number.parseInt(m[1], 10) : 0
}

function attrUri(line: string): string | null {
  const m = line.match(/URI="([^"]+)"/)
  return m ? m[1] : null
}

async function downloadHls(source: string, title: string, onProgress: DownloadProgress): Promise<{ blob: Blob; filename: string }> {
  onProgress(1, 'Fetching playlist…')
  let playlistUrl = source
  let text = await (await fetchOk(source)).text()

  // Master playlist -> pick the highest-bandwidth variant.
  if (text.includes('#EXT-X-STREAM-INF')) {
    const lines = text.split(/\r?\n/)
    let best = { bw: -1, uri: '' as string }
    for (let i = 0; i < lines.length; i++) {
      if (!lines[i].startsWith('#EXT-X-STREAM-INF')) continue
      const uri = (lines[i + 1] || '').trim()
      if (!uri || uri.startsWith('#')) continue
      const bw = attrNumber(lines[i], 'BANDWIDTH')
      if (bw > best.bw) best = { bw, uri }
    }
    if (!best.uri) throw new Error('No variants in playlist')
    playlistUrl = absolute(best.uri, source)
    text = await (await fetchOk(playlistUrl)).text()
  }

  if (text.includes('#EXT-X-KEY') && !/METHOD=NONE/.test(text)) {
    throw new Error('Ei stream encrypted — download possible na')
  }

  const lines = text.split(/\r?\n/)
  let initUrl: string | null = null
  const segments: string[] = []
  for (const raw of lines) {
    const line = raw.trim()
    if (!line) continue
    if (line.startsWith('#EXT-X-MAP')) {
      const uri = attrUri(line)
      if (uri) initUrl = absolute(uri, playlistUrl)
      continue
    }
    if (line.startsWith('#')) continue
    segments.push(absolute(line, playlistUrl))
  }
  if (!segments.length) throw new Error('Playlist has no segments')

  const parts: Uint8Array[] = []
  let total = segments.length
  let done = 0

  if (initUrl) {
    onProgress(1, 'Fetching init segment…')
    parts.push(new Uint8Array(await (await fetchOk(initUrl)).arrayBuffer()))
    total += 0
  }

  const CONCURRENCY = 4
  for (let i = 0; i < segments.length; i += CONCURRENCY) {
    const chunk = segments.slice(i, i + CONCURRENCY)
    const buffers = await Promise.all(
      chunk.map(async (s) => new Uint8Array(await (await fetchOk(s)).arrayBuffer()))
    )
    parts.push(...buffers)
    done += chunk.length
    const pct = Math.round((done / total) * 100)
    onProgress(pct, `Downloading ${done}/${total} segments`)
  }

  onProgress(99, 'Saving file…')
  const isFmp4 = !!initUrl
  const blob = new Blob(parts as BlobPart[], { type: isFmp4 ? 'video/mp4' : 'video/mp2t' })
  return { blob, filename: sanitizeFilename(title, isFmp4 ? 'mp4' : 'ts') }
}

function downloadDirect(source: string, filename: string): void {
  const a = document.createElement('a')
  a.href = apiUrl(`/api/download?dl=1&u=${encodeURIComponent(source)}&f=${encodeURIComponent(filename)}`)
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
}

export function isEmbedSource(result: ResolveResult): boolean {
  const provider = result.provider || ''
  const source = result.source || ''
  return (
    provider === 'youtube' ||
    provider === 'vimeo' ||
    source.includes('youtube.com/embed') ||
    source.includes('player.vimeo.com') ||
    source.includes('terabox.com/sharing/') ||
    source.includes('diskwala.com/')
  )
}

export async function downloadResult(
  result: ResolveResult,
  onProgress: DownloadProgress = noop
): Promise<{ ok: boolean; message?: string; needsShare?: boolean }> {
  const source = result.source || ''
  if (!source) return { ok: false, message: 'Kono source nei.' }

  if (isEmbedSource(result)) {
    return {
      ok: false,
      message: 'Ei link embed mode-e play hoy (YouTube/Vimeo/TeraBox-iframe/DiskWala) — direct download app-e support na.'
    }
  }

  if (result.type === 'hls') {
    onProgress(0, 'Starting…')
    const { blob, filename } = await downloadHls(source, result.title || 'video', onProgress)
    return saveSmart(blob, filename)
  }

  if (result.type === 'dash') {
    return { ok: false, message: 'DASH (.mpd) download ekhono support na — HLS/MP4 link paste korun.' }
  }

  if (['mp4', 'webm', 'mov', 'direct'].includes(result.type || '')) {
    const ext =
      result.type === 'webm' ? 'webm' : result.type === 'mov' ? 'mov' : result.type === 'mp4' ? 'mp4' : 'mp4'
    const filename = sanitizeFilename(result.title, ext)
    if (isIOS() && shareFilesSupported()) {
      // iOS needs the file in memory to offer "Save Video" -> Photos app.
      onProgress(0, 'Fetching video…')
      const res = await fetchOk(source)
      const blob = await res.blob()
      onProgress(99, 'Preparing save…')
      return saveSmart(blob, filename)
    }
    downloadDirect(source, filename)
    return { ok: true }
  }

  return { ok: false, message: `Ei type (${result.type}) download support na.` }
}
